import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { motion } from 'motion/react'
import { ArrowDown, ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { heroImages } from '@/constants/content'
import { Reveal, RevealText } from '@/components/generals/reveal'
import { useLenis } from '@/components/generals/smooth-scroll'
import { TransitionLink } from '@/components/generals/page-transition'
import { useIntroHandoff, useIntroReady } from '@/components/generals/intro'

const SOFT_EASE = [0.22, 1, 0.36, 1] as const

/**
 * Three portraits side by side under the giant wordmark, the middle one
 * taller than the two beside it. Each wrapper is marked `data-intro-target`
 * so the opening cards can land exactly on it; during that handoff the
 * portraits appear in place instead of playing their own fade and rise.
 */
const PortraitRow: FC = (): ReactNode => {
    const ready = useIntroReady()
    const handoff = useIntroHandoff()

    return (
        <div className='flex items-start gap-2.5 px-4 md:gap-5 md:px-[30px]'>
            {heroImages.map((image, index) => (
                <div
                    key={image.src}
                    data-intro-target={index}
                    className={cn('relative min-w-0 flex-1', index === 1 ? 'aspect-[2/3]' : 'aspect-[11/12]')}
                >
                    <motion.div
                        className='absolute inset-0 overflow-hidden rounded-[10px] bg-surface'
                        initial={{ opacity: 0, y: 48 }}
                        animate={ready ? { opacity: 1, y: 0 } : handoff ? { opacity: 0, y: 0 } : undefined}
                        transition={handoff ? { duration: 0 } : { duration: 1.2, delay: 0.1 + index * 0.16, ease: SOFT_EASE }}
                    >
                        <Image
                            src={image.src}
                            alt={image.alt}
                            fill
                            preload
                            quality={85}
                            sizes='(min-width: 768px) 32vw, 30vw'
                            className='object-cover'
                        />
                    </motion.div>
                </div>
            ))}
        </div>
    )
}

export const Hero: FC = (): ReactNode => {
    const lenis = useLenis()

    const scrollOn = () => {
        const next = document.getElementById('about-band')

        if (!next) return

        if (lenis) lenis.scrollTo(next, { duration: 1.6 })
        else next.scrollIntoView({ behavior: 'smooth' })
    }

    return (
        <section id='hero' aria-labelledby='hero-title' className='pt-18'>
            <h1 className='sr-only'>Auren</h1>

            <div className='px-4 pb-5 pt-2 md:px-[30px] md:pb-8 md:pt-3'>
                <div aria-hidden='true' className='h-[var(--hero-wordmark-h,26vw)]' />
            </div>

            <PortraitRow />

            <div className='grid gap-14 px-4 pb-28 pt-20 md:grid-cols-2 md:gap-10 md:px-[30px] md:pb-44 md:pt-32'>
                <div className='flex items-start gap-5 md:gap-10'>
                    <button
                        type='button'
                        onClick={scrollOn}
                        className='bob mt-2 flex size-10 shrink-0 items-center justify-center rounded-full text-foreground transition-colors hover:text-gold md:mt-3'
                    >
                        <ArrowDown className='size-5' aria-hidden='true' />

                        <span className='sr-only'>
                            Scroll to the next section
                        </span>
                    </button>

                    <RevealText
                        id='hero-title'
                        as='h2'
                        text='Designer making brands and websites people remember.'
                        className='max-w-[15ch] text-[clamp(2.25rem,3.9vw,3.75rem)] font-light leading-[1.02] tracking-[-0.045em] text-foreground'
                    />
                </div>

                <div className='max-w-md md:justify-self-start md:pl-[6%] lg:pl-[12%]'>
                    <Reveal y={20}>
                        <p className='inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-white/[0.03] px-3.5 py-1.5 text-[13px] text-foreground/90'>
                            <span className='pulse-dot' aria-hidden='true' />
                            Open for new projects
                        </p>
                    </Reveal>

                    <Reveal y={20} delay={0.08}>
                        <p className='mt-8 leading-relaxed text-muted-foreground'>
                            I am Aurelia, an independent designer in London. I shape identities, websites and digital products with clear ideas, careful typography and a little bit of nerve.
                        </p>
                    </Reveal>

                    <Reveal y={20} delay={0.14}>
                        <p className='mt-5 leading-relaxed text-muted-foreground'>
                            I work with founders, studios and cultural teams who want their work to feel considered, not generic.
                        </p>
                    </Reveal>

                    <Reveal y={20} delay={0.2}>
                        <TransitionLink
                            href='/contact'
                            className='group mt-10 inline-flex items-center gap-3 text-sm font-medium text-foreground'
                        >
                            <ArrowRight className='size-4 text-gold transition-transform duration-500 group-hover:translate-x-1' aria-hidden='true' />

                            <span className='relative'>
                                Let&apos;s work together
                                <span aria-hidden='true' className='absolute -bottom-1 left-0 h-px w-full bg-white/20' />
                                <span aria-hidden='true' className='absolute -bottom-1 left-0 h-px w-full origin-left scale-x-0 bg-gold transition-transform duration-500 group-hover:scale-x-100' />
                            </span>
                        </TransitionLink>
                    </Reveal>
                </div>
            </div>
        </section>
    )
}
